import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
// Import custom components
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';

// Import actions and types from Redux slices
import { AppDispatch, RootState } from 'store/store';

// Import utility functions
import { getStatusNameById, getStatusColor } from 'utils/visitorsHelper';

// Import constants
import { DATE_TIME_AM_PM_FORMAT } from 'constants/dates';
import { TABLE_HEIGHT } from 'constants/ui';

import { formatFullName, hasPermission, statusKeyMap } from 'utils/utils'
import { setSelectedVisit } from 'store/slices/visitsSlice';
import { getRequest } from 'api/apiClient';
import { useTranslation } from 'react-i18next';
import { VisitStatus } from 'types/visitTypes';

interface VisitorSearchTableProps {
    setDrawerVisible: (visible: boolean) => void;
}

const VisitorSearchTable: React.FC<VisitorSearchTableProps> = ({ setDrawerVisible }) => {
    // Redux hooks
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);

    const { visitors, searchVisitorsLoading } = useSelector((state: RootState) => state.visitorSearch);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasAllvisitorsRead = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:r');

    const [visitStatus, setVisitStatus] = useState<VisitStatus[]>([]);
    const [statusFilterOptions, setStatusFilterOptions] = useState<{ label: string; value: string }[]>([]);
    const [defaultStatusFilter, setDefaultStatusFilter] = useState<string[]>([]);



    const { t } = useTranslation();

    useEffect(() => {
        const fetchVisitStatuses = async () => {
            try {
                const visitorStatus = await getRequest(`/orgs/${orgId}/visitorStatus`);
                setVisitStatus(visitorStatus.data);
            } catch (e) {
                console.error('Failed to fetch visit statuses', e);
            }
        };

        if (orgId) {
            fetchVisitStatuses();
        }
    }, [orgId]);

    useEffect(() => {
        if (visitStatus.length > 0) {
            const options = [
                ...visitStatus.map(status => ({
                    label: t(status.languageCode ?? '', status.name ?? ''),
                    value: status.name,
                })),
                {
                    label: t('vst_data_noshow', 'No Show'),
                    value: 'No Show',
                },
            ];

            setStatusFilterOptions(options);
            setDefaultStatusFilter(options.map(option => option.value));
        }
    }, [visitStatus, t]);

    // Format visitors data
    const formattedVisitors = visitors.data
        .filter(visitor => hasAllvisitorsRead || globalUserId === visitor.hostUserId)
        // .filter(visitor => {
        //     // Check if the visitor's id exists in the visits array
        //     const matchingVisit = visits.find(v => Number(v.id) === Number(visitor.visitId));
        //     return matchingVisit;
        // })
        .map(visitor => ({
            id: visitor.id,
            fullName: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
            dateTime: visitor.timestamp ? dayjs(visitor.timestamp).format(DATE_TIME_AM_PM_FORMAT) : null,
            email: visitor.email,
            mobilePhone: visitor.mobilePhone,
            company: visitor.company,
            status: getStatusNameById(visitor.status, visitor.timestamp),
            timestamp: visitor.timestamp,
            visitId: visitor.visitId,
            visitStatus: visitor.visitStatus,
            key: `${visitor.id}-${visitor.visitId}`, // Create a unique key by combining visitor.id and visitId
        }))
        .sort((a, b) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix()); // Sort by timestamp

    // Table columns definition

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'fullName',
            label: t('visitorSearch.table.fullName'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''),
        },
        {
            dataIndex: 'email',
            label: t('visitorSearch.table.email'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
            hidden: true
        },
        {
            dataIndex: 'company',
            label: t('visitorSearch.table.company'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.company || '').localeCompare(b.company || ''),
        },
        {
            dataIndex: 'status',
            label: t('visitorSearch.table.status'),
            filter: {
                type: 'multiSelect',
                options: statusFilterOptions,
            },
            defaultFilteredValue: defaultStatusFilter,
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
            render: (value) => {
                const color = getStatusColor(value);
                const translated = t(statusKeyMap[value] || value);
                return <div style={{ backgroundColor: color, padding: '5px 10px', borderRadius: '4px', color: 'white' }}>
                    {translated}
                </div>;
            }
        },
        {
            dataIndex: 'dateTime',
            label: t('visitorSearch.table.dateTime'),
            sorter: (a, b) => dayjs(a.dateTime, DATE_TIME_AM_PM_FORMAT).unix() - dayjs(b.dateTime, DATE_TIME_AM_PM_FORMAT).unix(),
        },
    ];

    if (statusFilterOptions.length === 0 || defaultStatusFilter.length === 0) {
        return null;
    }

    // Table properties
    const opTableProps: IOpTableProps = {
        dataSource: formattedVisitors,
        label: t('visitorSearch.table.label'),
        columns: columns,
        rowActions: {
            onEditClick: async (visit: any) => {
                const selectedVisit = await getRequest(`/orgs/${orgId}/visitor/${visit.visitId}`);
                dispatch(setSelectedVisit(selectedVisit.data[0]));
                setDrawerVisible(true);
            }
        },
        height: TABLE_HEIGHT,
        loading: searchVisitorsLoading,
        allowGlobalSearch: false,
        allowExport: true,
        allowShowHideColumns: true,
        rowKey: 'key', // Use the unique key
    };

    return (
        <OpSpace direction="vertical" size="middle" style={{ display: 'flex' }}>
            <OpTable {...opTableProps} />
        </OpSpace>
    );
}

export default VisitorSearchTable;
